import { inject, Injectable } from "@angular/core";
import { DriverDto } from "@dtos/driver.dto";
import { DriverDocumentDto } from "@dtos/driver-document.dto";
import { InvalidationReason } from "@models/invalidation-reason.model";
import { ApiService } from "@services/api.service";
import { EndPoints } from "@services/EndPoints";
import { ClientCertificationsResponseDto, BulkUpdateCertificationDto } from "@dtos/certifications.dto";

//INTERFAZ PARA RESPUESTA PAGINADA
export interface PaginatedDriversResponse {
  count: number;
  total_pages: number;
  next: string | null;
  previous: string | null;
  results: DriverDto[];
}

@Injectable({ providedIn: 'root' })
export class DriversProvider {

  private readonly api = inject(ApiService);

  /**
   * OBTENER CONDUCTORES CON SOPORTE PARA FILTRADO, BUSQUEDA, ORDENAMIENTO Y PAGINACION
   * @param queryParams - OBJETO CON TODOS LOS PARAMETROS DE CONSULTA
   * @returns RESPUESTA PAGINADA CON CONDUCTORES Y TOTAL
   * TODO: MOVER INTERFACE
   */
  async getDrivers(queryParams: Record<string, any> = {}): Promise<PaginatedDriversResponse | null> {
    try {
      //ESTABLECER VALORES POR DEFECTO SI NO SE PROPORCIONAN
      const params = {
        page: 1,
        page_size: 50,
        ...queryParams
      };

      //REALIZAR LA PETICION AL BACKEND
      return await this.api.get<PaginatedDriversResponse>({
        url: EndPoints.getDrivers,
        queryParams: params
      });
    } catch (error) {
      console.error('Error al obtener conductores:', error);
      return null;
    }
  }

  /**
   * OBTENER UN CONDUCTOR POR ID
   * @param driverId - ID DEL CONDUCTOR
   * @returns DATOS DEL CONDUCTOR
   */
  async getDriverById(driverId: number): Promise<DriverDto | null> {
    try {
      return await this.api.get<DriverDto>({
        url: EndPoints.getDriver.replace('{driverId}', String(driverId))
      });
    } catch (error) {
      console.error('Error al obtener conductor por ID:', error);
      return null;
    }
  }

  /**
   * CREAR UN NUEVO CONDUCTOR
   * @param driverData - DATOS DEL CONDUCTOR A CREAR
   * @param imageFile - ARCHIVO DE IMAGEN (OPCIONAL)
   * @returns CONDUCTOR CREADO
   */
  async createDriver(driverData: Partial<DriverDto>, imageFile?: File | null): Promise<DriverDto | null> {
    try {
      // PREPARAR LOS DATOS PARA ENVIAR
      const formParams: Record<string, any> = {
        name: driverData.name || '',
        last_name: driverData.last_name || '',
        username: driverData.email || '', // EL USERNAME ES EL EMAIL
        email: driverData.email || '',
        dni: driverData.dni || '',
        nie: driverData.nie || '',
        cif: driverData.cif || '',
        phone: driverData.phone || '',
        address: driverData.address || '',
        postal_code: driverData.postal_code || '',
        city: driverData.city || '',
        province: driverData.province || '',
        country: driverData.country || 'ES',
        is_active: driverData.is_active ?? true,
        validated: driverData.validated ?? true,
        transport_blocked: driverData.transport_blocked ?? false,
        billing_blocked: driverData.billing_blocked ?? false,
        allows_access_location: driverData.allows_access_location ?? true,
        rating: driverData.rating || ''
      };

      // AGREGAR TAGS SI EXISTEN (COMO STRING SEPARADO POR COMAS)
      if (driverData.tags && Array.isArray(driverData.tags) && driverData.tags.length > 0) {
        // CONVERTIR EL ARRAY DE TAGS A STRING SEPARADO POR COMAS
        const tagIds = driverData.tags.map((tag: any) =>
          typeof tag === 'number' ? tag : tag.id
        );
        formParams['tags'] = tagIds.join(',');
      }

      // PREPARAR LOS ARCHIVOS SI HAY IMAGEN
      const fileParams: { [key: string]: File } = {};
      if (imageFile && imageFile.size > 0) {
        fileParams['image'] = imageFile;
      }

      // REALIZAR LA PETICION POST
      return await this.api.post<DriverDto>({
        url: EndPoints.createDriver,
        formParams,
        fileParams: Object.keys(fileParams).length > 0 ? fileParams : undefined
      });
    } catch (error) {
      console.error('Error al crear conductor:', error);
      return null;
    }
  }

  /**
   * ACTUALIZAR UN CONDUCTOR EXISTENTE
   * @param driverId - ID DEL CONDUCTOR A ACTUALIZAR
   * @param driverData - DATOS DEL CONDUCTOR A ACTUALIZAR
   * @param imageFile - ARCHIVO DE IMAGEN (OPCIONAL)
   * @returns CONDUCTOR ACTUALIZADO
   */
  async updateDriver(driverId: number, driverData: Partial<DriverDto>, imageFile?: File | null): Promise<DriverDto | null> {
    try {
      // PREPARAR LOS DATOS PARA ENVIAR (SOLO LOS CAMPOS QUE SE MODIFICARON)
      const formParams: Record<string, any> = {};

      // AGREGAR SOLO LOS CAMPOS QUE ESTÁN DEFINIDOS
      if (driverData.name !== undefined) formParams['name'] = driverData.name;
      if (driverData.last_name !== undefined) formParams['last_name'] = driverData.last_name;
      if (driverData.email !== undefined) {
        formParams['email'] = driverData.email;
        formParams['username'] = driverData.email; // EL USERNAME ES EL EMAIL
      }
      if (driverData.dni !== undefined) formParams['dni'] = driverData.dni;
      if (driverData.nie !== undefined) formParams['nie'] = driverData.nie;
      if (driverData.cif !== undefined) formParams['cif'] = driverData.cif;
      if (driverData.phone !== undefined) formParams['phone'] = driverData.phone;
      if (driverData.address !== undefined) formParams['address'] = driverData.address;
      if (driverData.postal_code !== undefined) formParams['postal_code'] = driverData.postal_code;
      if (driverData.city !== undefined) formParams['city'] = driverData.city;
      if (driverData.province !== undefined) formParams['province'] = driverData.province;
      if (driverData.country !== undefined) formParams['country'] = driverData.country;
      if (driverData.is_active !== undefined) formParams['is_active'] = driverData.is_active;
      if (driverData.validated !== undefined) formParams['validated'] = driverData.validated;
      if (driverData.transport_blocked !== undefined) formParams['transport_blocked'] = driverData.transport_blocked;
      if (driverData.billing_blocked !== undefined) formParams['billing_blocked'] = driverData.billing_blocked;
      if (driverData.allows_access_location !== undefined) formParams['allows_access_location'] = driverData.allows_access_location;
      if (driverData.rating !== undefined) formParams['rating'] = driverData.rating;
      if (driverData.business_name !== undefined) formParams['business_name'] = driverData.business_name;

      // AGREGAR TAGS SI EXISTEN (COMO STRING SEPARADO POR COMAS)
      if (driverData.tags !== undefined && Array.isArray(driverData.tags)) {
        if (driverData.tags.length > 0) {
          const tagIds = driverData.tags.map((tag: any) =>
            typeof tag === 'number' ? tag : tag.id
          );
          formParams['tags'] = tagIds.join(',');
        } else {
          formParams['tags'] = '';
        }
      }

      // PREPARAR LOS ARCHIVOS SI HAY IMAGEN
      const fileParams: { [key: string]: File } = {};
      if (imageFile && imageFile.size > 0) {
        fileParams['image'] = imageFile;
      }

      // REALIZAR LA PETICION PATCH
      return await this.api.patch<DriverDto>({
        url: EndPoints.updateDriver.replace('{driverId}', String(driverId)),
        formParams,
        fileParams: Object.keys(fileParams).length > 0 ? fileParams : undefined
      });
    } catch (error) {
      console.error('Error al actualizar conductor:', error);
      return null;
    }
  }

  /**
   * ELIMINAR UN CONDUCTOR
   * @param driverId - ID DEL CONDUCTOR A ELIMINAR
   * @returns TRUE SI SE ELIMINO CORRECTAMENTE, FALSE SI HUBO ERROR
   */
  async deleteDriver(driverId: number): Promise<boolean> {
    try {
      await this.api.delete({
        url: EndPoints.deleteDriver.replace('{driverId}', String(driverId))
      });
      return true;
    } catch (error) {
      console.error('Error al eliminar conductor:', error);
      return false;
    }
  }

  /**
   * ACTUALIZAR UN DOCUMENTO DE CONDUCTOR
   * @param documentId - ID DEL DOCUMENTO A ACTUALIZAR
   * @param document - DATOS DEL DOCUMENTO
   * @param driverId - ID DEL CONDUCTOR
   * @returns DOCUMENTO ACTUALIZADO
   */
  async updateDriverDocument(
    documentId: number,
    document: DriverDocumentDto,
    driverId: number
  ): Promise<DriverDocumentDto | null> {
    try {
      const formParams: Record<string, any> = {
        title: document.title,
        doc_type: document.doc_type,
        document_status: document.document_status,
        expiration_date: document.expiration_date || '',
        invalidation_reason: document.invalidation_reason || '',
        invalidation_reason_type: document.invalidation_reason_type || '',
        driver: driverId
        // No incluimos el campo 'file' para no sobrescribir el archivo existente
      };

      return await this.api.patch<DriverDocumentDto>({
        url: EndPoints.updateDriverDocument.replace('{documentId}', String(documentId)),
        formParams
      });
    } catch (error) {
      console.error('Error al actualizar documento de conductor:', error);
      return null;
    }
  }

  /**
   * VALIDAR UN DOCUMENTO DE CONDUCTOR
   * @param documentId - ID DEL DOCUMENTO A VALIDAR
   * @param expirationDate - FECHA DE EXPIRACIÓN DEL DOCUMENTO
   * @returns DOCUMENTO VALIDADO
   */
  async validateDriverDocument(
    documentId: number,
    expirationDate: string
  ): Promise<any> {
    try {
      const formParams: Record<string, any> = {
        expiration_date: expirationDate
      };

      return await this.api.put<any>({
        url: EndPoints.validateDriverDocument.replace('{documentId}', String(documentId)),
        formParams
      });
    } catch (error) {
      console.error('Error al validar documento de conductor:', error);
      return null;
    }
  }

  /**
   * OBTENER MOTIVOS DE INVALIDACIÓN DE DOCUMENTOS
   * @returns LISTA DE MOTIVOS DE INVALIDACIÓN
   */
  async getInvalidationReasons(): Promise<InvalidationReason[]> {
    try {
      const reasons = await this.api.get<InvalidationReason[]>({
        url: EndPoints.getDriverDocumentInvalidationReasons
      });
      return reasons || [];
    } catch (error) {
      console.error('Error al obtener motivos de invalidación:', error);
      return [];
    }
  }

  /**
   * INVALIDAR UN DOCUMENTO DE CONDUCTOR
   * @param documentId - ID DEL DOCUMENTO A INVALIDAR
   * @param invalidationReason - MENSAJE DE INVALIDACIÓN
   * @param invalidationReasonType - TIPO DE MOTIVO DE INVALIDACIÓN
   * @returns RESPUESTA DE LA INVALIDACIÓN
   */
  async invalidateDriverDocument(
    documentId: number,
    invalidationReason: string,
    invalidationReasonType: string
  ): Promise<any> {
    console.log('=== invalidateDriverDocument llamado ===');
    console.log('documentId:', documentId);
    console.log('invalidationReason:', invalidationReason);
    console.log('invalidationReasonType:', invalidationReasonType);

    try {
      const formParams: Record<string, any> = {
        invalidation_reason: invalidationReason,
        invalidation_reason_type: invalidationReasonType
      };

      console.log('formParams preparados:', formParams);

      const url = EndPoints.invalidateDriverDocument.replace('{documentId}', String(documentId));
      console.log('URL de la request:', url);

      const result = await this.api.put<any>({
        url: url,
        formParams
      });

      console.log('Respuesta del backend:', result);
      return result;
    } catch (error) {
      console.error('Error al invalidar documento de conductor:', error);
      return null;
    }
  }

  /**
   * OBTENER CERTIFICACIONES DE UN CONDUCTOR POR CLIENTES
   * @param driverId - ID DEL CONDUCTOR
   * @param clientIds - IDS DE LOS CLIENTES (HASTA 5)
   * @returns LISTA DE CERTIFICACIONES POR CLIENTE
   */
  async getDriverCertificationsByClients(
    driverId: number,
    clientIds: number[]
  ): Promise<ClientCertificationsResponseDto[] | null> {
    try {
      // Validar que no se supere el límite de 5 clientes
      if (clientIds.length > 5) {
        console.warn('Se pueden solicitar máximo 5 clientes a la vez');
        clientIds = clientIds.slice(0, 5);
      }

      // Construir el query param client_ids como string separado por comas
      const queryParams = {
        client_ids: clientIds.join(',')
      };

      return await this.api.get<ClientCertificationsResponseDto[]>({
        url: EndPoints.getDriverClientCertifications.replace('{driverId}', String(driverId)),
        queryParams
      });
    } catch (error) {
      console.error('Error al obtener certificaciones de conductor:', error);
      return null;
    }
  }

  /**
   * ACTUALIZAR CERTIFICACIONES DE UN CONDUCTOR EN MASA
   * @param driverId - ID DEL CONDUCTOR
   * @param certifications - ARRAY DE CERTIFICACIONES A ACTUALIZAR
   * @returns TRUE SI SE ACTUALIZÓ CORRECTAMENTE, FALSE SI HUBO ERROR
   */
  async bulkUpdateDriverCertifications(
    driverId: number,
    certifications: BulkUpdateCertificationDto[]
  ): Promise<boolean> {
    try {
      await this.api.post({
        url: EndPoints.bulkUpdateDriverCertifications.replace('{driverId}', String(driverId)),
        body: certifications
      });
      return true;
    } catch (error) {
      console.error('Error al actualizar certificaciones de conductor:', error);
      return false;
    }
  }

  /**
   * EXPORTAR CONDUCTORES A CSV
   * DESCARGA UN ARCHIVO CSV CON TODOS LOS CONDUCTORES
   * @returns BLOB DEL ARCHIVO CSV
   */
  async exportDriversCSV(queryParams: Record<string, any> = {}): Promise<Blob | null> {
    try {
      const blob = await this.api.downloadFile({
        url: EndPoints.exportDriversCSV,
        queryParams
      });

      // CREAR UN ENLACE TEMPORAL PARA DESCARGAR EL ARCHIVO
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'drivers.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return blob;
    } catch (error) {
      console.error('Error al exportar conductores a CSV:', error);
      return null;
    }
  }
}
